import TryUpload from "../components/TryUpload";
import TrilliumIcon from "../components/TrilliumIcon";
import PublicPageHeader from "../components/PublicPageHeader";
import useDocumentMeta from "../lib/useDocumentMeta";

/**
 * /lesson-from-resource — dedicated, indexable page for the upload-a-
 * resource flow (Part B of the SEO URLs lane, Jul 2026).
 *
 * This is the same page previously served at /try. /try now
 * client-side-redirects here via <Navigate replace> in App.js (SPA
 * soft-redirect — NOT an HTTP 301; the preview + prod hosts don't yet
 * expose edge-level redirects for frontend paths, so this is the best
 * we can do until they do; see build report).
 *
 * The upload tool (`<TryUpload />`) is UNCHANGED — same
 * one-free-then-sign-in behaviour, same mechanism. This page only
 * changes the URL, the title/meta, and the intro heading + paragraph.
 *
 * Locked copy (title, meta, H1 + intro paragraph) is founder-approved
 * word-for-word; the pytest tripwire
 * `test_lesson_from_resource_page.py` enforces this and asserts that
 * every keyword term ("resource", "reading", "article", "passage",
 * "worksheet", "PDF") is preserved.
 */
export default function TryPage() {
  useDocumentMeta({
    title:
      "Turn Any Resource into an Ontario Lesson — Free | Atomic Edu",
    description:
      "Upload a reading, article, or worksheet and get a full Ontario lesson built around it. Your first one is free. Grades 4–8.",
  });

  return (
    <div className="min-h-screen bg-[#eef8f4] font-montserrat">
      {/* Shared slate-navy public header — brand logo top-left, links
          to /. Same header renders on /free-lesson-generator. */}
      <PublicPageHeader />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-8">
        {/* Hero — LOCKED COPY B. Word-for-word. The keyword terms
            "resource", "reading", "article", "passage", "worksheet",
            "PDF" are all preserved deliberately for search. */}
        <section className="text-center sm:text-left" data-testid="try-hero">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#4a6984]/15 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/80">
            <TrilliumIcon size={12} className="text-[#4a6984]" />
            For Ontario Grade 4–8 Teachers
          </span>
          <h1
            data-testid="try-headline"
            className="mt-5 font-black text-4xl sm:text-5xl text-[#1c2d3d] tracking-tighter leading-[1.05]"
          >
            Turn a resource or reading into an Ontario lesson
          </h1>
          <p
            data-testid="try-intro"
            className="mt-5 text-[#4a6984] font-medium text-base sm:text-lg leading-relaxed max-w-2xl mx-auto sm:mx-0"
          >
            Upload what you&rsquo;re already working from &mdash; an
            article, a reading passage, a worksheet, a PDF &mdash; and
            get a full Ontario lesson built around it, in three-part
            structure with a matching student handout. Your first one is
            free.
          </p>
        </section>

        <TryUpload />

        <p
          data-testid="try-social-proof"
          className="text-center text-[11px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/55"
        >
          Ontario curriculum-aligned <span className="mx-2">·</span>
          Grades 4–8 <span className="mx-2">·</span>
          Skills-first pedagogy <span className="mx-2">·</span>
          7-day free trial
        </p>
      </main>
    </div>
  );
}
